import { SeriesZonesOptionsObject } from "highcharts";
import { useMemo } from "react";

import { LineGraphWidgetConfiguration } from "@/lib/api";
import COLORS from "@/features/shared/widgets/graphs/colors";

export const useThresholdZones = (configuration: LineGraphWidgetConfiguration) => {
  return useMemo<SeriesZonesOptionsObject[]>(() => {
    if (!configuration.thresholds) return [];
    const thresholds = configuration.thresholds.sort((a, b) =>
      a.value > b.value ? 1 : -1,
    );
    const min = thresholds.filter((t) => t.threshold_type === "min");
    const max = thresholds.filter((t) => t.threshold_type === "max");

    const mappedMin = min.map((threshold) => ({
      value: threshold.value,
      color:
        threshold.color && threshold.color in COLORS.POINTS
          ? COLORS.POINTS[threshold.color as keyof typeof COLORS.POINTS]
          : COLORS.POINTS.blue,
    }));

    const mappedMax = max.reduce<SeriesZonesOptionsObject[]>(
      (acc, threshold, index, array) => {
        const pointColor = array[index - 1]?.color;
        const color =
          pointColor && pointColor in COLORS.POINTS
            ? COLORS.POINTS[pointColor as keyof typeof COLORS.POINTS]
            : undefined;
        // start with zone without color (default)
        if (acc.length === 0 && !color) acc.push({ color, value: threshold.value });

        // add zone to next threshold is available
        const next = array[index + 1];
        const nextColor =
          threshold.color && threshold.color in COLORS.POINTS
            ? COLORS.POINTS[threshold.color as keyof typeof COLORS.POINTS]
            : COLORS.POINTS.red;
        if (next) {
          acc.push({ color: nextColor, value: next.value });
        } else {
          acc.push({ color: nextColor });
        }
        return acc;
      },
      [],
    );

    // order should be min to max
    // value of each zone is the max value of that zone
    // last zone has no value
    return [...mappedMin, ...mappedMax];
  }, [configuration.thresholds]);
};
